import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router'
import {
  getEldTerms,
  onboardCarrier,
  onboardOauth,
  carrierSignupOnboarding,
  updateLastCheckedEld,
  syncIntercomOnboardingProgress,
} from 'api'
import {
  Container,
  Modal,
  Icon,
  TypoGraph,
  Button,
  Toast as toast,
} from 'components'
import { InputFormField } from 'components/hookForm'
import { CheckBoxField } from 'components/controllers'
import {
  checkEldVendor,
  evaluateOAuth,
  buildMotiveUrl,
  buildCookies,
  removeAllCookies,
  buildSamsaraCookies,
  buildSamsaraUrl,
  checkIsCanal,
  buildCanalRedirection,
  checkIsOneNetwork,
  evaluateCompanyId,
  checkIsCoverwhaleTest
} from '../oauthHelper'
import { requiredData, insurerDataItems } from 'constants/cob'
import { showIntercom, credentialFailureCountFromPendingAttempts } from 'tools/intercom'
import styles from './authorize.module.scss'
import colors from 'theme/colors'
import { CircularProgress, Backdrop } from '@mui/material'
import { GetCookie } from 'tools/cookies'
import WidgetSuccessPage from '../successPage'
import { isEldProviderForSimulation } from 'tools/customBehaviors'
import { reportException } from 'tools/logs'
import { BiStacked, WIDGET_STRINGS } from 'pages/tcwidget/bilingualStrings'

const cobImgUrl = process.env.REACT_APP_COB_IMAGE_URL

interface IAuthorizeModalProps {
  id?:string
  open: boolean
  selectedEld: any
  invitationKey: string | null
  carrierId: string | number | null
  cobListData: any
  eldIndex?: any
  cookieData?: any
  canalRedirection?: any
  oneNetworkCustomBehavior?: any
  coverwhaleTestCustomBehavior?: any
  isSimulation?: boolean
  sessionOnboardedElds: any[]
  setSessionOnboardedElds: React.Dispatch<React.SetStateAction<any[]>>
  handleClose: () => void
}

const AuthorizeModal: React.FC<IAuthorizeModalProps> = (props) => {
  const {
    id,
    open,
    handleClose,
    selectedEld,
    invitationKey,
    carrierId,
    cobListData,
    eldIndex,
    cookieData,
    canalRedirection,
    oneNetworkCustomBehavior,
    coverwhaleTestCustomBehavior,
    isSimulation,
    sessionOnboardedElds,
    setSessionOnboardedElds,
  } = props
  const eldDataSharing = selectedEld?.eldDataSharing || {}
  const isInsurer = cobListData?.invitationInfo?.isInsurance === true

  const [showPermissions, setShowPermissions] = useState(false)
  const [step, setStep] = useState(0)
  const [termsUrl, setTermsUrl] = useState('')
  const [eldFormData, setEldFormData] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [doneBtnLoader, setDoneBtnLoader] = useState(false)
  const [authorizeStatus, setAuthorizeStatus] = useState(0) // 0 - shows normal screen with t&c, 1 - shows success modal content, 2 - shows credential failed modal content, 3 - shows modal loader
  const [mobileView, setMobileView] = useState(window.innerWidth < 1000)
  const [driverPermissionReady, setDriverPermissionReady] = useState(false)
  const [hosPermissionReady, setHosPermissionReady] = useState(false)
  const [successfulOnboarding, setSuccessfulOnboarding] = useState(false)
  const [fvInvitationToken, setFvInvitationToken] = useState(null);
  const [loadingTermsAndCondition, setLoadingTermsAndCondition] = useState<boolean>(false)
  const insurerListRef = useRef<HTMLDivElement | null>(null)
  const insurerChevronRef = useRef<HTMLDivElement | null>(null)
  const insurerOverlayRef = useRef<HTMLDivElement | null>(null)
  const scrollTopRef = useRef(0)
  const showScrollHintRef = useRef(true)
  const fvUrl = process.env.REACT_APP_FV_URL

  const navigate = useNavigate()

  const companyProfile = useSelector(
    (state: any) => state?.whiteLabel?.companyProfile
  )

  const methods = useForm<any>({
    reValidateMode: 'onChange',
  })

  const { handleSubmit, reset } = methods

  useEffect(() => {
    if (cookieData && cobListData) {
      setAuthorizeStatus(3)
      setLoading(true)
      setEldFormData((prev: any) => ({
        ...prev.eldFormData,
        eldDataSharing: {
          ...cookieData?.eldDataSharing,
        },
      }))
      const companyId = evaluateCompanyId(cobListData, selectedEld, false)
      const payLoad: any = {
        invitationToken: cookieData?.invitationKey,
        code: cookieData?.code,
        companyId: companyId,
        eldName: cookieData?.eldVendor,
        eldDataSharing: cookieData?.eldDataSharing,
      }
      if (checkIsCanal()) {
        payLoad.postbackStatus = {
          url: cookieData?.canalUrl ?? canalRedirection?.url,
          type: 'GET',
          state: cookieData?.canalState ?? canalRedirection?.state,
        }
      }
      else if (checkIsOneNetwork()) {
        payLoad.postbackStatus = {
          url: 'https://rtvn9499.onenetwork.com/oms/rest/LNTL/lntl-tranzlogix/NotifyCarrier',
          type: 'POST',
          data: [
            '[CARRIER_NAME]|' + (oneNetworkCustomBehavior ? oneNetworkCustomBehavior.carrierName : null),
            '[ELD_PROVIDER]|' + (oneNetworkCustomBehavior ? oneNetworkCustomBehavior.eldProvider : null),
            '[SCAC]|'+ (oneNetworkCustomBehavior ? oneNetworkCustomBehavior.scac : null),
          ],
        }
      }
      else if (checkIsCoverwhaleTest()) {
        payLoad.postbackStatus = {
          url: 'https://hkdk.events/3yhd3bkli6u5pa',
          type: 'POST',
          usesAdditionalData: true,
          data: [
            '[POLICY_ID]|' + (coverwhaleTestCustomBehavior ? coverwhaleTestCustomBehavior.policyId : GetCookie('policyId', true)),
          ],
        }
      }
      onboardOauth(payLoad)
        .then((res: any) => {
          setFvInvitationToken(res?.invitationToken)
          toast({ title: res?.message })
          setAuthorizeStatus(1)
          removeAllCookies(false, false, false)
        })
        .catch((error: any) => {
          reportException(error)
          setAuthorizeStatus(2)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [cookieData, cobListData])

  useEffect(() => {
    if (!doneBtnLoader && !successfulOnboarding) {
      const defaultValues = selectedEld?.eldMetadataParams?.map((item: any) => {
        if (!item?.paramIsVisible) return null
        return {
          [item.paramName]: item?.paramDefaultValue || '',
        }
      })
      const formData = Object.assign({}, ...(defaultValues || []))
      reset(formData)
      setShowPermissions(false)
      if (isInsurer) {
        const sharing = selectedEld?.eldDataSharing || {}
        setEldFormData({
          ...selectedEld,
          eldDataSharing: {
            ...sharing,
            hoursOfServiceData: false,
            vehicleData: sharing.vehicleData === true || sharing.vehicleDataEnabled === true,
            locationData: sharing.locationData === true || sharing.locationDataEnabled === true,
            driverData: sharing.driverData === true || sharing.driverDataEnabled === true,
            engineData: sharing.engineData === true || sharing.engineDataEnabled === true,
            dvirData: sharing.dvirData === true || sharing.dvirDataEnabled === true,
            authenticationData: sharing.authenticationData === true || sharing.authenticationDataEnabled === true,
            safetyEventsData: sharing.safetyEventsData === true || sharing.safetyEventsDataEnabled === true,
          },
        })
      } else {
        setEldFormData(selectedEld)
      }
      setStep(0)
      setLoadingTermsAndCondition(true)
      if (!!open) {
        getEldTerms(invitationKey, selectedEld?.eldVendor).then((res: any) => {
          setTermsUrl(res?.url || '')
        }).catch((error: any) => {
          reportException(error)
        }).finally(() => {
          setLoadingTermsAndCondition(false)
        });
        if (open && selectedEld && invitationKey) {
          updateLastCheckedEld(invitationKey, selectedEld?.eldVendorId)
            .catch((error: any) => {
              reportException(error)
            })
          syncIntercomOnboardingProgress(invitationKey, { tspSelected: selectedEld?.eldVendor })
            .catch((error: any) => {
              reportException(error)
            })
        }
      }
    }
  }, [open, invitationKey, selectedEld, doneBtnLoader, successfulOnboarding, reset])

  useEffect(() => {
    const handleWindowResize = () => {
      setMobileView(window.innerWidth < 1000)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  })
  
  useEffect(() => {
    if (driverPermissionReady === true && hosPermissionReady === true) {
      const companyId = evaluateCompanyId(cobListData, selectedEld, true)
      const oAuthEldIndex = checkEldVendor(selectedEld)
      if (oAuthEldIndex === 1) {
        if(!!selectedEld.isFvOauth){
          const selectedEldFv = cobListData?.removedElds?.find(
            (info: { eldProviderId: number }) => info.eldProviderId === selectedEld?.eldFvProviderId) ?? selectedEld;
          buildCookies(
            eldIndex,
            invitationKey,
            eldFormData?.eldDataSharing,
            selectedEldFv
          )
          const redirectUrl = window.location.protocol+"//"+fvUrl+ '/cob/fv?invitationToken='+ invitationKey+"&eldProviderId="+selectedEld?.eldProviderId
          window.location.href = redirectUrl
        }else{
          buildCookies(
            eldIndex,
            invitationKey,
            eldFormData?.eldDataSharing,
            selectedEld
          )
  
          window.location.href = buildMotiveUrl(companyId, selectedEld)
        }   
      } else if (oAuthEldIndex === 2) {
        const csrf = buildSamsaraCookies(
          eldIndex,
          invitationKey,
          eldFormData?.eldDataSharing,
          selectedEld
        )
        window.location.href = buildSamsaraUrl(companyId, selectedEld, csrf)
      }
      else setStep(1)
    }
  }, [driverPermissionReady, hosPermissionReady])

  const handleConfirmPermission = () => {
    setShowPermissions(false)
  }

  const onSubmitCredentials = (formData: any) => {
    setLoading(true)
    let payLoad = { ...eldFormData }
    Object.entries(formData).forEach(([key, val]) => {
      payLoad?.eldMetadataParams?.map((item: any) => {
        if (item?.paramName === key) {
          item['paramValue'] = val
        }
        return item
      })
    })
    payLoad = {
      carrierInfo: {
        carrierId: carrierId,
        carrierName: (oneNetworkCustomBehavior ? oneNetworkCustomBehavior.carrierName : 
                  coverwhaleTestCustomBehavior ? coverwhaleTestCustomBehavior.carrierName : null),
        carrierCodes: cobListData?.carrierInfo?.carrierCodes,
      },
      contactInfo: cobListData?.contactInfo,
      invitationInfo: cobListData?.invitationInfo,
      eldInfo: [payLoad],
    }
    if (checkIsCanal() === true) {
      payLoad.postbackStatus = {
        url: cookieData?.canalUrl ?? canalRedirection?.url,
        type: 'GET',
        state: cookieData?.canalState ?? canalRedirection?.state,
      }
    }
    else if (checkIsOneNetwork() === true) {
      payLoad.postbackStatus = {
        url: '',
        type: 'POST',
        state: '',
        data: [
          '[CARRIER_NAME]|' + (oneNetworkCustomBehavior ? oneNetworkCustomBehavior.carrierName : null),
          '[ELD_PROVIDER]|' + (oneNetworkCustomBehavior ? oneNetworkCustomBehavior.eldProvider : null),
          '[SCAC]|'+ (oneNetworkCustomBehavior ? oneNetworkCustomBehavior.scac : null),
        ],
      }
    }
    else if (checkIsCoverwhaleTest() === true) {
      payLoad.postbackStatus = {
        url: 'https://hkdk.events/3yhd3bkli6u5pa',
        type: 'POST',
        state: '',
        usesAdditionalData: true,
        data: [
          '[POLICY_ID]|' + (coverwhaleTestCustomBehavior ? coverwhaleTestCustomBehavior.policyId : GetCookie('policyId', true)),
        ],
      }
    }
    onboardCarrier(invitationKey, payLoad)
      .then((res: any) => {
        toast({id:"carrierOnboardingToast", title: res?.message })
        const onboardedEld = cobListData?.eldInfo?.find(
          (eld: any) => eld.eldProviderId === selectedEld?.eldProviderId
        )
        if (onboardedEld) {
          onboardedEld.overallStatus = 'VERIFIED'
        }
        setSessionOnboardedElds((prev) => {
          const isAlreadyAdded = prev.some((eld) => eld.eldProviderId === selectedEld?.eldProviderId)
          if (!isAlreadyAdded) {
            const updatedList = [...prev, selectedEld]
            return updatedList
          }
          return prev
        })    
        setAuthorizeStatus(1)
        syncIntercomOnboardingProgress(invitationKey, {
          onboardingStatus: 'onboarded',
          credentialFailureCount: credentialFailureCountFromPendingAttempts(res?.invitationInfo?.pendingAttempts),
        }).catch((error: any) => {
          reportException(error)
        })
      })
      .catch((error: any) => {
        reportException(error)
        setAuthorizeStatus(2)
        syncIntercomOnboardingProgress(invitationKey, {
          onboardingStatus: 'credentials_failed',
          credentialFailureCount: credentialFailureCountFromPendingAttempts(
            error?.response?.data?.invitationInfo?.pendingAttempts
          ),
        }).catch((error: any) => {
          reportException(error)
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const setOAuthPermissions = (name: string, checked: boolean) => {
    setEldFormData((prev: any) => ({
        ...prev,
        eldDataSharing: {
          ...prev.eldDataSharing,
          [name]: checked,
        },
    }))
    if (name === 'driverData') setDriverPermissionReady(true)
    else if (name === 'hoursOfServiceData') setHosPermissionReady(true)
  }

  const handleCheckboxSelection = (e: any, name: any) => {
    setEldFormData((prev: any) => ({
      ...prev,
      eldDataSharing: {
        ...prev.eldDataSharing,
        [name]: e.target.checked,
      },
    }))
  }

  const handleDone = () => {
    setDoneBtnLoader(true)
    const inviteKey = !!cobListData?.invitationInfo.hasFreightVisibility
      && !!fvInvitationToken
      ? fvInvitationToken
      : invitationKey
    carrierSignupOnboarding(inviteKey)
      .then((res: any) => {
        setAuthorizeStatus(0)
        setStep(0)
        setShowPermissions(false)
        reset({})
        handleClose()
        syncIntercomOnboardingProgress(inviteKey, { onboardingStatus: 'enrolled' })
          .catch((error: any) => {
            reportException(error)
          })
        if (checkIsCanal() && res?.data?.postbackSent !== true) {
          const canalRedirection = buildCanalRedirection(200)
          removeAllCookies(true, false, false)
          window.location.href = canalRedirection
        } else if (oneNetworkCustomBehavior || checkIsOneNetwork()) {
          removeAllCookies(false, true, false)
          setSuccessfulOnboarding(true)
        } else if (coverwhaleTestCustomBehavior || checkIsCoverwhaleTest()) {
          removeAllCookies(false, false, true)
          setSuccessfulOnboarding(true)
        } else {
          removeAllCookies(true, true, true)
          setSuccessfulOnboarding(true)
        }
      })
      .catch((error: any) => {
        reportException(error)
        const statusCode = error?.response?.status
        if (checkIsCanal()) {
          const canalRedirection = buildCanalRedirection(statusCode)
          window.location.href = canalRedirection
        }
      })
      .finally(() => {
        setDoneBtnLoader(false)
      })
  }

  const handleAddAnotherEld = () => {
    removeAllCookies(false, false, false)
    setAuthorizeStatus(0)
    setStep(0)
    handleClose()
  }

  const handleTryAgain = () => {
    if (cookieData) {
      const oAuthEldIndex = checkEldVendor(selectedEld)
      const companyId = evaluateCompanyId(cobListData, selectedEld, true)
      const isOAuth = evaluateOAuth(cobListData?.invitationInfo, selectedEld)
      if (isOAuth && isOAuth === true) {
        if (oAuthEldIndex === 1) {
          if(!!selectedEld.isFvOauth){
            const selectedEldFv = cobListData?.removedElds?.find(
              (info: { eldProviderId: number }) => info.eldProviderId === selectedEld?.eldFvProviderId) ?? selectedEld;
            buildCookies(
              eldIndex,
              invitationKey,
              eldFormData?.eldDataSharing,
              selectedEldFv
            )
            window.location.href = window.location.protocol+"//"+fvUrl+ '/cob/fv?invitationToken='+ invitationKey+"&eldProviderId="+selectedEld?.eldProviderId
          }else{
            buildCookies(
              eldIndex,
              invitationKey,
              eldFormData?.eldDataSharing,
              selectedEld
            )
            window.location.href = buildMotiveUrl(companyId, selectedEld)
          }  
        } else if (oAuthEldIndex === 2) {
          const csrf = buildSamsaraCookies(
            eldIndex,
            invitationKey,
            eldFormData?.eldDataSharing,
            selectedEld
          )
          window.location.href = buildSamsaraUrl(companyId, selectedEld, csrf)
        }
      } else {
        setAuthorizeStatus(0)
        setStep(1)
      }
    } else {
      setAuthorizeStatus(0)
      setStep(1)
    }
  }

  const handleAuthorize = async () => {
    if (!isSimulation || (isSimulation === true && isEldProviderForSimulation(selectedEld.eldVendor) === false)) {
      const isOAuth = evaluateOAuth(cobListData?.invitationInfo, selectedEld)
      if (isOAuth === true) {
        // Manually set permissions in case of OAuth
        setOAuthPermissions('driverData', true)
        setOAuthPermissions('hoursOfServiceData', true)

        await new Promise((resolve) => setTimeout(resolve, 0))
      } else setStep(1)
    }
  }

  const RenderInputField = (props: any) => {
    const { item } = props
    if (!item?.paramIsVisible) return <></>
    switch (item?.paramName) {
      case 'password':
        return (
          <InputFormField
              id={"eldPassword"}
            type="password"
            name={item?.paramName}
            label={item?.paramName}
            required={item?.paramIsRequired}
          />
        )
      default:
        return (
          <InputFormField
              id={"eldApikey"}
            name={item?.paramName}
            label={item?.paramName}
            required={item?.paramIsRequired}
          />
        )
    }
  }

  const handleOpenEldHelp = () => window.open('https://truckercloud.zendesk.com/hc/en-us/articles/4415549969435')

  const AuthorizeComponent = () => {
    return (
      <Container>
        <Container
          className={`${styles.formContainer} ${
            mobileView && styles.mobileFormContainer
          }`}
        >
          <FormProvider {...methods}>
            {selectedEld?.eldMetadataParams?.map((item: any) => (
              <RenderInputField item={item} />
            ))}
          </FormProvider>
        </Container>
        <Container
          className={`${styles.infoContainer} ${
            mobileView && styles.mobileInfoContainer
          }`}
        >
          <Icon name="icon_info" />
          <TypoGraph>
            <TypoGraph
              variant="subtitle2"
              component="span"
              content="Learn how to obtain this information from "
            />
            <TypoGraph
              variant="subtitle2"
              component="span"
              content={selectedEld?.eldVendorDisplayName}
              color={colors.primary}
              onClick={handleOpenEldHelp}
              link
            />
          </TypoGraph>
          <Icon
            name="icon_redirect"
            color={colors.primary}
            onClick={handleOpenEldHelp}
            iconButton
          />
        </Container>
        <Container className={styles.authorizeBtnContainer}>
          <Button id={"backVerifyCredentialsButton"} variant="outlined" label="Back" onClick={() => setStep(0)} />
          <Button
              id={"verifyCredentialsButton"}
            label="Verify Credentials"
            loading={loading}
            disabled={loading}
            onClick={handleSubmit(onSubmitCredentials)}
          />
        </Container>
      </Container>
    )
  }

  const InsurerAuthorizeComponent = () => {
    return (
      <Container className={styles.insurerAuthorizeWrapper}>
        <Container
          className={`${styles.formContainer} ${styles.insurerFormSingleColumn} ${
            mobileView && styles.mobileFormContainer
          }`}
        >
          <FormProvider {...methods}>
            {selectedEld?.eldMetadataParams?.map((item: any) => {
              if (!item?.paramIsVisible) return null
              return (
                <div key={item.paramName} className={styles.insurerInputWrapper}>
                  <label className={styles.insurerInputLabel}>{item.paramName}</label>
                  <InputFormField
                    id={item.paramName === 'password' ? 'eldPassword' : 'eldApikey'}
                    type={item.paramName === 'password' ? 'password' : 'text'}
                    name={item.paramName}
                    required={item.paramIsRequired}
                    placeholder={`Enter your ${item.paramName}`}
                    className={styles.insurerInput}
                    fullWidth
                  />
                </div>
              )
            })}
          </FormProvider>
        </Container>
        <div className={styles.insurerFormFooter}>
          <Container data-testid="insurerInfoBox" className={styles.insurerInfoBox}>
            <div data-testid="insurerChatbotIcon" className={styles.intercomIconWrapper} style={{cursor: 'pointer'}} onClick={showIntercom}>
              <Icon name="icon_intercom" size={16} />
            </div>
            <TypoGraph>
              <TypoGraph variant="subtitle2" component="span" content="Having trouble? " />
              <TypoGraph id="insurerChatbotLink" variant="subtitle2" component="span" content="Open the chat" onClick={showIntercom} color={colors.primary} link sx={{textDecoration: 'underline'}} />
              <TypoGraph variant="subtitle2" component="span" content=" for help onboarding your provider." />
            </TypoGraph>
          </Container>
          <Container className={styles.insurerFormBtnFixed}>
            <Button id={"backVerifyCredentialsButton"} variant="outlined" label="Back" onClick={() => setStep(0)} />
            <Button
                id={"verifyCredentialsButton"}
              label="Verify Credentials"
              loading={loading}
              disabled={loading}
              onClick={handleSubmit(onSubmitCredentials)}
            />
          </Container>
        </div>
      </Container>
    )
  }

  const PermissionComponent = () => {
    const isOAuth = evaluateOAuth(cobListData?.invitationInfo, selectedEld)
    return (
      <>
        <Container
          className={`${styles.sectionListContainer} ${
            mobileView && styles.mobileSectionList
          }`}
        >
          <Container className={styles.actionSectionCentered}>
            <TypoGraph
              variant="subtitle1"
              content="Required Data"
              className={styles.sectionTitle}
            />
            <Container className={styles.requireContainer}>
              {requiredData.map((item: any) => (

                <Container key={item.title} className={styles.requireItem}>
                  <Icon name={item.icon} color={colors.black} size={30} />
                  <Container>
                    <TypoGraph content={item.title} />
                    <TypoGraph
                      variant="subtitle2"
                      content={item.content}
                      color={colors.grey}
                    />
                  </Container>
                </Container>
              ))}
            </Container>
          </Container>
          <Container className={styles.actionSection}>
            <TypoGraph
              variant="subtitle1"
              content="Suggested Data"
              className={styles.sectionTitle}
            />
            <Container className={styles.requireContainer}>
              {/* Driver Data */}
              <Container
                className={`${styles.requireItem} ${
                  showPermissions &&
                  !eldDataSharing?.driverDataEnabled &&
                  styles.disableContainer
                }`}
              >
                <Container className={styles.checkboxContainer}>
                  {showPermissions && (
                    <CheckBoxField
                        id={"driverData"}
                      onChange={(e) => handleCheckboxSelection(e, 'driverData')}
                      value={eldFormData?.eldDataSharing?.driverData}
                      checked={eldFormData?.eldDataSharing?.driverData}
                      disabled={!eldDataSharing?.driverDataEnabled}
                    />
                  )}
                  <Icon name="icon_profile" color={colors.black} size={30} />
                </Container>
                <Container>
                  <TypoGraph content="Driver Data" />
                  <TypoGraph
                    variant="subtitle2"
                    content="Driver ID’s, Email, Phone"
                    color={colors.grey}
                  />
                </Container>
              </Container>
              {/* HOS Data */}
              <Container
                className={`${styles.requireItem} ${
                  showPermissions &&
                  !eldDataSharing?.hoursOfServiceDataEnabled &&
                  styles.disableContainer
                }`}
              >
                <Container className={styles.checkboxContainer}>
                  {showPermissions && (
                    <CheckBoxField
                        id={"HOSData"}
                      onChange={(e) =>
                        handleCheckboxSelection(e, 'hoursOfServiceData')
                      }
                      value={eldFormData?.eldDataSharing?.hoursOfServiceData}
                      disabled={!eldDataSharing?.hoursOfServiceDataEnabled}
                      checked={eldFormData?.eldDataSharing?.hoursOfServiceData}
                    />
                  )}
                  <Icon name="cob_clock" color={colors.black} size={30} />
                </Container>
                <Container>
                  <TypoGraph content="Hours of Service" />
                  <TypoGraph
                    variant="subtitle2"
                    content="Hours of Service"
                    color={colors.grey}
                  />
                </Container>
              </Container>
              {/* Engine Data */}
              <Container
                className={`${styles.requireItem} ${
                  showPermissions &&
                  !eldDataSharing?.engineDataEnabled &&
                  styles.disableContainer
                }`}
              >
                <Container className={styles.checkboxContainer}>
                  {showPermissions && (
                    <CheckBoxField
                        id={"engineData"}
                      onChange={(e) => handleCheckboxSelection(e, 'engineData')}
                      value={eldFormData?.eldDataSharing?.engineData}
                      disabled={!eldDataSharing?.engineDataEnabled}
                    />
                  )}
                  <Icon name="cob_engine" color={colors.black} size={30} />
                </Container>
                <Container>
                  <TypoGraph content="Engine Data" />
                  <TypoGraph
                    variant="subtitle2"
                    content="Diagnostics & maintenance"
                    color={colors.grey}
                  />
                </Container>
              </Container>
              {/* Safety Data */}
              <Container
                className={`${styles.requireItem} ${
                  showPermissions &&
                  !eldDataSharing?.safetyEventsDataEnabled && 
                  styles.disableContainer
                }`}
              >
                <Container className={styles.checkboxContainer}>
                  {showPermissions && (
                    <CheckBoxField
                      id={"safetyEventsData"}
                      onChange={(e) => handleCheckboxSelection(e, 'safetyEventsData')}
                      value={eldFormData?.eldDataSharing?.safetyEventsData}
                      checked={eldFormData?.eldDataSharing?.safetyEventsData}
                      disabled={!eldDataSharing?.safetyEventsDataEnabled}
                    />
                  )}
                  <Icon name="cob_safety" color={colors.black} size={30} />
                </Container>
                <Container>
                  <TypoGraph content="Safety Events Data" />
                  <TypoGraph
                    variant="subtitle2"
                    content="Safety events"
                    color={colors.grey}
                  />
                </Container>
              </Container>
            </Container>
          </Container>
        </Container>
        <Container
          className={`${styles.permissionBtnContainer} ${
            mobileView && styles.mobilePermissionBtn
          }`}
        >
          {showPermissions ? (
            <Button
                id={"confirmPermissions"}
              variant="outlined"
              label="Confirm Permission"
              onClick={handleConfirmPermission}
            />
          ) : (
            <Button
                id={"checkPermissions"}
              variant="outlined"
              label="Permissions Management"
              onClick={() => setShowPermissions(true)}
            />
          )}
          <Button  id={"authorizeOnboard"} label="Authorize" onClick={handleAuthorize} />
        </Container>
      </>
    )
  }

  const applyScrollHintStyles = (visible: boolean) => {
    if (insurerChevronRef.current) {
      insurerChevronRef.current.style.opacity = visible ? '1' : '0'
      insurerChevronRef.current.style.pointerEvents = visible ? 'all' : 'none'
    }
    if (insurerOverlayRef.current) {
      insurerOverlayRef.current.style.opacity = visible ? '1' : '0'
    }
  }

  const setScrollHintVisibility = (visible: boolean) => {
    if (showScrollHintRef.current === visible) return
    showScrollHintRef.current = visible
    applyScrollHintStyles(visible)
  }

  useEffect(() => {
    if (isInsurer && open && insurerListRef.current) {
      const insurerListElement = insurerListRef.current
      setScrollHintVisibility(insurerListElement.scrollHeight > insurerListElement.clientHeight)
    }
  }, [isInsurer, open, step])

  const setInsurerListRef = useCallback((listElement: HTMLDivElement | null) => {
    insurerListRef.current = listElement
    if (listElement && scrollTopRef.current > 0) {
      listElement.scrollTop = scrollTopRef.current
    }
  }, [])

  const setInsurerChevronRefCb = useCallback((chevronElement: HTMLDivElement | null) => {
    insurerChevronRef.current = chevronElement
    if (chevronElement) {
      chevronElement.style.opacity = showScrollHintRef.current ? '1' : '0'
      chevronElement.style.pointerEvents = showScrollHintRef.current ? 'all' : 'none'
    }
  }, [])

  const setInsurerOverlayRefCb = useCallback((overlayElement: HTMLDivElement | null) => {
    insurerOverlayRef.current = overlayElement
    if (overlayElement) {
      overlayElement.style.opacity = showScrollHintRef.current ? '1' : '0'
    }
  }, [])

  const handleInsurerListScroll = () => {
    const insurerListElement = insurerListRef.current
    if (!insurerListElement) return
    scrollTopRef.current = insurerListElement.scrollTop
    const shouldShow = insurerListElement.scrollHeight - insurerListElement.scrollTop > insurerListElement.clientHeight + 5
    setScrollHintVisibility(shouldShow)
  }

  const handleScrollDown = () => {
    const insurerListElement = insurerListRef.current
    if (!insurerListElement) return
    const newTop = insurerListElement.scrollTop + 120
    scrollTopRef.current = newTop
    insurerListElement.scrollTop = newTop
  }

  const InsurerPermissionComponent = () => {
    return (
      <>
        <div className={styles.insurerListWrapper}>
          <div
            data-testid="insurerDataList"
            ref={setInsurerListRef}
            className={styles.insurerDataList}
            onScroll={handleInsurerListScroll}
          >
            {insurerDataItems.filter((item: any) => eldFormData?.eldDataSharing?.[item.dataKey] === true).map((item: any, index: number) => (
              <Container key={`${item.title}-${index}`} data-testid={`insurerDataItem-${item.dataKey}`} className={styles.insurerDataItem}>
                <Icon name={item.icon} color={colors.primary} size={20} boxClassName={styles.insurerIconWrapper} />
                <Container>
                  <TypoGraph content={item.title} />
                  <TypoGraph
                    variant="subtitle2"
                    content={item.content}
                    color={colors.grey}
                  />
                </Container>
              </Container>
            ))}
          </div>
          <div
            ref={setInsurerOverlayRefCb}
            className={styles.insurerListFadeOverlay}
            aria-hidden
          />
          <div
            ref={setInsurerChevronRefCb}
            className={styles.insurerScrollChevron}
            onClick={(e) => { e.stopPropagation(); handleScrollDown() }}
          >
            <Icon materialIcon="ExpandMore" color={colors.primary} size={24} />
          </div>
        </div>
        <Container
          className={`${styles.insurerAuthorizeBtnContainer} ${
            mobileView && styles.mobilePermissionBtn
          }`}
        >
          <Button
            id={"insurerAuthorizeOnboard"}
            label="Authorize Connection"
            onClick={handleAuthorize}
            className={styles.insurerAuthorizeBtn}
          />
        </Container>
      </>
    )
  }

  const formatEldList = (elds: any[]) => {
    if (!elds || elds.length === 0) return ''
    
    const vendors = elds.map(eld => eld.eldVendor || eld.eldVendorDisplayName)
    
    if (vendors.length === 1) {
      return vendors[0]
    } else if (vendors.length === 2) {
      return `${vendors[0]} and ${vendors[1]}`
    } else {
      const allButLast = vendors.slice(0, -1).join(', ')
      const last = vendors[vendors.length - 1]
      return `${allButLast}, and ${last}`
    }
  }
  const ModalContent = () => {
    return (
      <Container
        className={`${styles.modalContentContainer} ${
          mobileView && styles.mobileModalContent
        }`}
      >
        {!oneNetworkCustomBehavior && (
          <Icon
            name="icon_cancel"
            boxClassName={styles.cancelBtn}
            onClick={() => {
              handleClose()
              setAuthorizeStatus(0)
              setStep(0)
            }}
            iconButton
          />
        )}
        <Container
          className={`${styles.logoSwitchContainer} ${
            mobileView && styles.mobileLogoSwitch
          } ${isInsurer && styles.insurerLogoSwitch}`}
        >
          {companyProfile?.logo ? (
            <Icon
              type="images"
              url={companyProfile?.logo}
              boxClassName="fullLogo"
              width={200}
              height={50}
            />
          ) : (
            <Icon
              name={open ? 'icon_logoFullname' : 'icon_logo'}
              width={200}
              height={30}
            />
          )}
          <Icon
            name="alternate_arrow"
            size={mobileView ? 15 : 35}
            boxClassName={mobileView ? styles.mobileArrow : ''}
          />
          <Icon
            type="images"
            url={`${cobImgUrl}/${selectedEld?.eldVendor}`}
            size={100}
            boxClassName={styles.logoContainer}
          />
        </Container>
        {authorizeStatus === 1 && (
          <Container>
            <Container className={styles.titleContainer}>
              <TypoGraph
                variant="h2"
                sx={{ textAlign: 'center', mb: 1.5 }}
              >
                <BiStacked
                  en={WIDGET_STRINGS.verifiedTitle.en}
                  fr={WIDGET_STRINGS.verifiedTitle.fr}
                />
              </TypoGraph>
              <TypoGraph
                variant="body2"
                sx={{ textAlign: 'center' }}
              >
                <BiStacked
                  {...WIDGET_STRINGS.verifiedBody(selectedEld?.eldVendor ?? '')}
                />
              </TypoGraph>
              <Icon name="icon_dialog_success" size={mobileView ? 150 : 300} />
            </Container>
            <Container className={styles.permissionBtnContainer}>
              {!oneNetworkCustomBehavior && (
                <Button
                  id={'addOtherEld'}
                  variant="outlined"
                  label="Add Another ELD"
                  onClick={handleAddAnotherEld}
                />
              )}
              <Button
                id={'doneOnboard'}
                label="Done"
                loading={doneBtnLoader}
                onClick={handleDone}
              />
            </Container>
          </Container>
        )}
        {authorizeStatus === 2 && (
          <Container>
            <Container className={styles.titleContainer}>
              <TypoGraph
                variant="h2"
                sx={{ textAlign: 'center', mb: 1.5 }}
              >
                <BiStacked
                  en={WIDGET_STRINGS.failedTitle.en}
                  fr={WIDGET_STRINGS.failedTitle.fr}
                />
              </TypoGraph>
              <TypoGraph
                variant="body2"
                sx={{ textAlign: 'center' }}
              >
                <BiStacked
                  {...WIDGET_STRINGS.failedBody(selectedEld?.eldVendor ?? '')}
                />
              </TypoGraph>
              <Icon name="icon_dialog_warning" size={mobileView ? 150 : 300} />
            </Container>
            <Container className={styles.permissionBtnContainer}>
              <Button
                id={'tryToVerifyAgain'}
                label="Try Again"
                onClick={handleTryAgain}
              />
            </Container>
          </Container>
        )}
        {authorizeStatus === 3 && (
          <Container>
            <Container className={styles.titleContainer}>
              <TypoGraph
                variant="h2"
                content="Verifying Credentials"
                sx={{ textAlign: 'center' }}
              />
              <TypoGraph
                variant="body2"
                content={`We are verifying credentials, this should only take a moment.`}
                sx={{ textAlign: 'center' }}
              />
              <Container className={styles.verifyLoaderContainer}>
                <CircularProgress />
              </Container>
            </Container>
          </Container>
        )}
        {authorizeStatus === 0 && !loadingTermsAndCondition && (
          <div className={isInsurer ? (step === 0 ? styles.insurerContentFixed : styles.insurerContentFlexible) : undefined}>
            <Container className={styles.titleContainer}>
              <TypoGraph
                variant="h2"
                content={
                  isInsurer
                    ? 'Connect to Integration Partner'
                    : (cobListData?.invitationInfo?.invitingCompanyName ??
                        'TruckerCloud') +
                      ` will be able to connect to ${selectedEld?.eldVendor}`
                }
                className={
                  mobileView ? styles.authorizeTitle : styles.titleContainer
                }
                sx={{ lineHeight: 1.1, mb: 1.5 }}
              />
              <TypoGraph
                sx={{ textAlign: 'center' }}
                className={mobileView ? styles.subtitleContainer : ''}
              >
                <BiStacked
                  en={
                    <TypoGraph variant="body2" component="span">
                      {WIDGET_STRINGS.terms.en.prefix(
                        cobListData?.invitationInfo?.invitingCompanyName
                      )}
                      <TypoGraph
                        variant="body2"
                        component={mobileView ? 'div' : 'span'}
                        color={colors.primary}
                        onClick={() => window.open(termsUrl)}
                        link
                        sx={{ mt: mobileView ? 1.25 : 0 }}
                      >
                        {WIDGET_STRINGS.terms.en.linkText}
                      </TypoGraph>
                      {WIDGET_STRINGS.terms.en.suffix}
                    </TypoGraph>
                  }
                  fr={
                    <TypoGraph variant="body2" component="span">
                      {WIDGET_STRINGS.terms.fr.prefix}
                      <TypoGraph
                        variant="body2"
                        component={mobileView ? 'div' : 'span'}
                        color={colors.primary}
                        onClick={() => window.open(termsUrl)}
                        link
                        sx={{ mt: mobileView ? 1.25 : 0 }}
                      >
                        {WIDGET_STRINGS.terms.fr.linkText}
                      </TypoGraph>
                      {WIDGET_STRINGS.terms.fr.suffix(
                        cobListData?.invitationInfo?.invitingCompanyName
                      )}
                    </TypoGraph>
                  }
                />
              </TypoGraph>
            </Container>
            {isInsurer ? (
              <div className={styles.insurerStepWrapper}>
                {step === 0 && <InsurerPermissionComponent />}
                {step === 1 && <InsurerAuthorizeComponent />}
              </div>
            ) : (
              <>
                {step === 0 && <PermissionComponent />}
                {step === 1 && <AuthorizeComponent />}
              </>
            )}
          </div>
        )}
        {authorizeStatus === 0 && loadingTermsAndCondition && (
          <Container className={styles.loadingModal}>
            <CircularProgress />
          </Container>
        )}
      </Container>
    )
  }

  return (
    <>
      {(oneNetworkCustomBehavior && (
        <>
          <Backdrop open={open} className={styles.backdrop}>
            {successfulOnboarding === true ? (
              <WidgetSuccessPage
                messageTitle={'Onboarding completed!'}
                messageText={
                  sessionOnboardedElds.length > 0
                    ? `Congratulations! Your ${formatEldList(
                        sessionOnboardedElds
                      )} onboarding with TruckerCloud is complete. You may now close this window.`
                    : `Congratulations! Your ${selectedEld?.eldVendor} onboarding with TruckerCloud is complete. You may now close this window.`
                }
              />
            ) : (
              <Modal
                open={open}
                dom={ModalContent()}
                dismissOnClose={false}
                handleClose={() => {
                  handleClose()
                }}
                className={styles.showScrollbar}
                backdropFused={true}
              />
            )}
          </Backdrop>
        </>
      )) ||
        (successfulOnboarding === true ? (
          <WidgetSuccessPage
            messageTitle={'Onboarding completed!'}
            messageText={
              sessionOnboardedElds.length > 0
                ? `Congratulations! Your ${formatEldList(
                    sessionOnboardedElds
                  )} onboarding with TruckerCloud is complete. You may now close this window.`
                : `Congratulations! Your ${selectedEld?.eldVendor} onboarding with TruckerCloud is complete. You may now close this window.`
            }
          />
        ) : (
          <Modal
            open={open}
            handleClose={() => {
              handleClose()
              setAuthorizeStatus(0)
              setStep(0)
            }}
            dom={ModalContent()}
          />
        ))}
    </>
  )
}

export default AuthorizeModal
